import { finalize } from 'rxjs/operators';

import * as Configuration from 'src/ajs-app/app.constants.js';
import { showCharts } from '../charts/clicks-metric-charts';
import { copyInputToClipboard } from './helpers';
export function AbstractElementViewConstructor(services, callbacks, context, siteTypeConfig) {

  let joint = services.joint;

  const ANGULAR_AWARE_ELEMENT_ROOT = require('./abstract-views-root.html');
  const ANGULAR_AWARE_ELEMENT_WRAPPER_START = require('./abstract-views-start.html');
  const ANGULAR_AWARE_ELEMENT_WRAPPER_END = require('./abstract-views-end.html');

  return joint.dia.ElementView.extend({

    template: null, // traditional boxes override this property with some vanilla html
    angularTemplate: null, // angular-aware elements override this property instead
    isElementEditable: false, // angular-aware elements override this (and implement afterEditElement if necessary) to get an edit button
    isElementDeletable: false,
    editElementModalController: null,
    editElementModalTemplate: null,
    angularScope: null,
    /**
     * Allows to disable the trash icon.
     * @example for whatsapp it is not possible to remove the last section or the last answer because at least one is needed.
     */
    disableRemove: () => false,

    initialize: function () {
      joint.dia.ElementView.prototype.initialize.apply(this, arguments);
      // this.template needs to be set in the 'concrete' subclasses
      if (this.angularTemplate) {
        this.buildAndAppendElementAngularTemplate();
      } else {
        this.$box = angular.element(this.template);
      }
      // Update the box position whenever the underlying model changes.
      this.model.on('change', this.updateBox, this);

      // Remove the box when the model gets removed from the graph.
      this.model.on('remove', this.removeBox, this);
    },


    getParentType: function () {
      const parent = this.getParent();
      return parent ? parent.get('type') : null;
    },

    getParent: function () {
      if (this.model.getAncestors().length > 0) {
        const parent = this.model.getAncestors()[0];
        if (parent) {
          return parent;
        }
      }
      return null;
    },

    getParentView: function () {
      const parent = this.getParent();
      if (parent) {
        return this.paper.findViewByModel(parent);
      }
      return null;
    },

    getType: function () {
      return this.model.get('type');
    },

    render: function () {
      joint.dia.ElementView.prototype.render.apply(this, arguments);
      this.paper.$el.prepend(this.$box);
      // Update scale transform for html when the paper scales
      this.listenTo(this.paper, 'scale', this.updateBox);

      // And update the html position and scale once too
      this.updateBox();
      return this;
    },


    updateBox: function () {
      if (this.paper) {
        // Set the position and dimension of the box so that it covers the JointJS element.
        this.$box.css(this.cssForScaleSizeAndPosition(false));
      }
    },

    cssForScaleSizeAndPosition: function (isCSSHeightFromModelBBox) {
      const bbox = this.model.getBBox();
      const scale = joint.V(this.paper.viewport).scale();
      const css = {
        transform: `scale(${scale.sx},${scale.sy})`,
        transformOrigin: '0 0',
        width: bbox.width, // width is driven by the cell data
        left: bbox.x * scale.sx,
        top: bbox.y * scale.sy
      };
      if (isCSSHeightFromModelBBox) {
        css.height = bbox.height; // height is driven by the cell data
      } else {
        // height is driven by the html content
      }
      return css;
    },


    removeBox: function () {
      if (this.angularScope) {
        this.angularScope.$destroy();
      }
      this.$box.remove();
    },

    removeElement: function () {
      const parent = this.model.getAncestors()[0];
      this.model.remove(); // the serialized graph will be updated by a global handler for the 'remove' event on the graph
      this.paper.findViewByModel(parent).resizeBox();
    },

    resizeBox: function () {
      if (services.JointjsOptimisationsFactory.logCallsToResize()) {
        console.warn('AbstractElementView - resizeBox()');
      }
      // non-box elements have their height set by their parent box
      const parent = this.model.getAncestors()[0];
      this.paper.findViewByModel(parent).resizeBox();
    },


    /**
     * Opens a element edition modal, whose controller and markup should be defined by the subclasses
     */
    editElement: function () {
      services.$mdDialog.show({
        controller: this.editElementModalController,
        template: this.editElementModalTemplate,
        locals: {
          'context': context,
          'elementData': angular.copy(this.angularScope.elementData),
          'elementView': this,
          'siteTypeConfig': siteTypeConfig
        }
      }).then(modalResult => {
        if (modalResult.type === 'ok') {
          const oldElementData = this.angularScope.elementData;
          this.angularScope.elementData = modalResult.data;
          this.afterEditElement(oldElementData, this.angularScope.elementData);
          callbacks.updateSerializedGraph(this.model.graph);
        } else if (modalResult.type === 'delete') {
          this.removeElement();
        }
      });
    },


    /**
     * Allow subclasses to perform some work after the elementData is updated with the modal result,
     * but before the serialized graph is upadted
     */
    afterEditElement: function (oldElementData, newElementData) {
      // Nothing special in the abstract class
    },

    buildAndAppendElementAngularTemplate: function () {
      this.$box = angular.element(ANGULAR_AWARE_ELEMENT_ROOT);
      this.$box[0].classList.add(this.model.get('type').substring(5).toLowerCase()); // adds a css class to the div, which allows customizing fron children elements
      this.angularScope = this.newAngularScope();
      this.angularScope.elementView = this; // allow calling view methods directly from angular markup
      this.angularScope.context = context; // some directives context info, such as company id
      let template = ANGULAR_AWARE_ELEMENT_WRAPPER_START + this.angularTemplate + ANGULAR_AWARE_ELEMENT_WRAPPER_END;
      this.appendAngularTemplate(template, this.angularScope);

      // Resize svg and html whenever the element data changes, as it probably means angular will soon (or just has) updated the view
      this.angularScope.$watch(
        'elementData',
        () => {
          setTimeout(() => this.resizeBox(), 10); // give angular a chance to update the DOM in case we are called before the DOM udpate
          setTimeout(() => this.resizeBox(), 1000); // images take a while to load; let's have a best effort timeout
        },
        true); // deep equality

      // init element data on scope from the joinths element, and register listener that will update it there if it is updated on the scope
      this.angularScope.elementData = this.model.get('elementData');
      this.angularScope.$watch('elementData', elementData => this.model.set('elementData', elementData));
    },


    newAngularScope: function () {
      const $rootScope = angular.element(document).injector().get("$rootScope");
      return $rootScope.$new();
    },


    appendAngularTemplate: function (template, scope) {
      const $compile = angular.element(document).injector().get("$compile");
      this.$box.append($compile(template)(scope));
    }

  });
}

export function AbstractBoxElementViewConstructor(services, callbacks, context, siteTypeConfig) {

  let joint = services.joint;

  return joint.shapes.html.AbstractElementView.extend({

    initialize: function () {
      // Have the SVG element update its label's text, width and fill color
      this.model.updateLabel();

      joint.shapes.html.AbstractElementView.prototype.initialize.apply(this, arguments);

      this.$box.addClass('alc-joint-box-element');
      if (this.options?.model?.attributes?.type) {
        this.$box.attr('data-testid', this.options.model.attributes.type)
      }
      this.$readOnlyEnforcer = angular.element('<div class="read-only-enforcer"></div>');
      this.$readOnlyEnforcer.on('click', () => showCharts(services, callbacks.getScenarioClicksData, this.model.get("id")));

      const $scope = this.newAngularScope();
      $scope.menuIcon = "arrow_drop_down_circle";
      $scope.menuItems = [
        {
          label: "parameters/quick-replies/dropdown_link1",
          onClick: () => this.duplicateBox(),
          enabled: this.model.get("canBeDuplicated")
        },
        {
          label: "parameters/quick-replies/dropdown_link2",
          onClick: () => this.testBox(false),
          enabled: this.model.get("canBeTested")
        },
        {
          label: "parameters/quick-replies/dropdown_link4",
          onClick: () => this.testBox(true),
          enabled: this.model.get("canBeTested")
        },
        {
          label: "parameters/quick-replies/dropdown_link3",
          onClick: () => this.deleteBox(),
          enabled: this.model.get("canBeDeleted"),
          showAsDisabled: this.showDeleteItemAsDisabled()
        },
        {
          label: "parameters/quick_replies/details/dropdown_metadata_to_clipboard",
          onClick: () => this.copyMetadataToClipboard(),
          enabled: !this.model.get("disableCopyLink"),
        },
        {
          label: "parameters/quick-replies/export_errors",
          onClick: () => this.exportlastErrors(),
          enabled: this.model.get("canExportErrors"),
        },
      ];
      const template = `<dropdown-box-menu menu-icon="menuIcon" data-testid="${this.options?.model?.attributes?.type}" menu-items="menuItems"></dropdown-box-menu>`;
      this.appendAngularTemplate(template, $scope);
    },


    render: function () {
      joint.shapes.html.AbstractElementView.prototype.render.apply(this, arguments);
      this.paper.$el.prepend(this.$readOnlyEnforcer);
      return this;
    },

    updateBox: function () {
      this.model.updateValidityInUI();
      if (this.paper) {
        // Set the position and dimension of the box so that it data-testid="html.FollowingConversationsBox"covers the JointJS element.
        const css = this.cssForScaleSizeAndPosition(true);
        this.$box.css(css);
        // Adjust read-only layout to include root label
        const scale = joint.V(this.paper.viewport).scale();
        css.height += 28;
        css.top -= Math.round(28 * scale.sx);
        this.$readOnlyEnforcer.css(css);
      }
    },

    removeBox: function (evt) {
      joint.shapes.html.AbstractElementView.prototype.removeBox.apply(this, arguments);
      this.$readOnlyEnforcer.remove();
    },


    // override this method for boxes that don't use an angular template but wish not to reimplement resizeBox()
    heightOfHTMLContent: function () {
      return this.$box.find('.alc-joint-box-content-height-measurer').outerHeight();
    },

    // TODO Pascal have as many subclasses use this as possible
    resizeBox: function () {
      if (services.JointjsOptimisationsFactory.logCallsToResize()) {
        console.warn('AbstractBoxElementView - resizeBox()');
      }
      let box = this.model;
      let embeddedCells = box.getEmbeddedCells();
      let paddingTop = this.heightOfHTMLContent();

      //Reposition and resize every item
      let ypos = paddingTop;
      for (let child of embeddedCells) {
        let childView = this.paper.findViewByModel(child);
        child.position(0, ypos, { parentRelative: true });
        let childHeight = childView.$box.outerHeight();
        child.resize(box.attributes.size.width, childHeight);
        ypos += childHeight;
      }
      if (embeddedCells.length === 0) {
        box.resize(box.get('size').width, paddingTop);
      } else {
        box.fitEmbeds({ padding: { top: paddingTop, right: 0, bottom: 0, left: 0 } }); // this will resize the box too
      }
      this.updateBox(); // apply new height to html element too
      callbacks.resizePaper();
    },


    duplicateBox: function () {
      const boxType = this.model.get('type');
      if (boxType === 'html.NoteBox' && (context.noteBoxesSet?.size || 0) >= context.noteLimitation) {
        services.AlertingFactory.error('parameters/quick_replies/details/graph/note/limit-reached');
        return;
      }
      const serializedGraph = callbacks.serializeGraph(this.model.graph);

      services.QuickRepliesFactory.copyBox(context.companyId, context.site.id, context.scenarioId, this.model.id, serializedGraph)
        .then(result => {
          for (let elementId in result.data.elements) {
            const position = result.data.elements[elementId].position;
            if (position) {
              position.x += 300;
              position.y -= 30;
            }
          }
          callbacks.deserializeGraph(services, joint, result.data, this.model.graph, this.paper, callbacks.linkFactory, context, () => { })
          services.AlertingFactory.success('parameters/quick_replies/details/duplicate_box_success');
        }, () => services.AlertingFactory.error('parameters/quick_replies/details/duplicate_box_error'));
    },

    // Generate an `messenger.com/t` link and open it in a new window to test the box or just copy the test link to clipboard
    testBox: function (onlyCopyLink) {
      services.QuickRepliesFactory.getPostbackPayload(context.companyId, context.site.id, context.scenarioId, this.model.get("id"))
        .then(result => {
          const link = "https://messenger.com/t/" + context.site.authorized_user.external_id + "?ref=" + result.data.postback_payload;

          if (onlyCopyLink) {
            const messages = {
              success: 'parameters/quick_replies/details/copy_test_link_success',
              error: 'parameters/quick_replies/details/copy_test_link_error'
            };
            copyInputToClipboard(services, link, messages);

            return;
          };

          window.open(link, '_blank').focus();
        }, () => {
          services.AlertingFactory.error("output/unexpected_behaviour");
        });
    },


    // Ask for user confirmation before deleting a box
    deleteBox: function () {
      let confirmDialogTranslationKey = 'parameters/quick_replies/details/bzc_ad/confirm_deletion_text';
      switch (this.model.get('type')) {
        case 'html.ClickToMessengerBox':
          confirmDialogTranslationKey = 'parameters/quick_replies/details/facebook_ad/confirm_deletion_text';
          break;
      }
      services.ConfirmationFactory.materialConfirmDialog(confirmDialogTranslationKey)
        .then(async () => {
          const canDeleteBox = await this.checkSpecificDataBeforeDeleteBox();
          if (canDeleteBox) {
            this.model.remove();
          }
        });
    },

    checkSpecificDataBeforeDeleteBox: async function () {
      return Promise.resolve(true);
    },

    // Another item in the dropdown menu, for all boxes
    copyMetadataToClipboard: function () {
      services.QuickRepliesFactory.getPostbackPayload(context.companyId, context.site.id, context.scenarioId, this.model.get('id'))
        .then(result => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(result.data.postback_payload)
              .then(() => services.AlertingFactory.success('parameters/quick_replies/details/copy_metadata_to_clipboard_sucessful'),
                () => services.AlertingFactory.error('bug/unexpected-error'));
          } else {
            services.AlertingFactory.error('error/feature_not_supported_in_browser');
          }
        },
          services.AlertingFactory.httpErrorHandler());
    },

    exportlastErrors: function () {
      const { site, scenarioId } = context;
      const siteId = site.id;
      const boxId = this.model.attributes.id;
      services.AngularSpinnerService.showSpinner();
      services.AngularAsyncRequestService
        .get(`${Configuration.constants.baseUrl}api/1.0/companies/${context.companyId}/sites/${siteId}/scenarios/${scenarioId}/export-errors/box-id/${boxId}`)
        .pipe(finalize(() => services.AngularSpinnerService.hideSpinner()))
        .subscribe({
          next: response => {
            if (response) {
              const { content, content_type, content_filename } = response;
              services.MediasFactory.downloadBinaryBase64(content, content_type, content_filename);
            }
          },
          error: services.AlertingFactory.httpErrorHandler()
        });
    },

    showDeleteItemAsDisabled: function () {
      // for some site types (twitter, igd and gbm) we allow deleting welcome message 
      // box if scenario type is satisfactionSurvey. This condition usually comes from backend,
      // here we allow item to be deleted only for already existing scenarios. 
      return (this.model.get("can_be_deleted") === false && !context.isSatisfactionSurvey);
    }

  });
}


/**
 * Common code for boxes whose child elements are reorganizable with drag-and-drop
 * (such as answers in a QuestionBox, and menu item in a SimpleEntryPointBox)
 */
export function AbstractBoxElementWithDragAndDropViewConstructor(services, callbacks, context, siteTypeConfig) {

  let joint = services.joint;

  return joint.shapes.html.AbstractBoxElementView.extend({

    /**
     * Returns the "padding" between the top of this box content and its first embedded child.
     * Must be overriden if the box does display content (like most boxes do)
     */
    paddingTop: function () {
      return 0;
    },

    /**
     * Override if a subclass needs to do something after children have been reordered.
     * For example questions with choice will want to renumber each answer.
     */
    afterReorderChildren: function () {
      // nothing by default
    },

    /**
     * by default all children are draggable.
     * Overriden for example by ListPicker because the "unexpected-user-input" special answer must always be at the beginning
     */
    isChildAlwaysFirst: function (child) {
      return false;
    },

    /**
     * by default all children are draggable.
     * Overriden for example by QuestionBoxView because the "user-input" special answer must always be at the end
     */
    isChildAlwaysLast: function (child) {
      return false;
    },

    /**
     * Returns this box's children cells, 
     * omitting the last child if there is one and if it must stay at the end and not be drag-and-dropped
     */
    draggableChildren: function () {
      return this.model.getEmbeddedCells().filter(child => !this.isChildAlwaysFirst(child) && !this.isChildAlwaysLast(child));
    },

    /**
     * Called during drag-and-drop, when the dragged child's index changes.
     * Repositions children according to their index in the embeddedCells.
     * The given child is not repositionned (because it is the one being dragged).
     */
    repositionOtherChildren: function (skippedChild) {
      let y = this.paddingTop();
      const alwaysFirstChild = this.getAlwaysFirstChild(this.model.getEmbeddedCells());
      // if there's a child always first, we must position other children just after it
      y += (alwaysFirstChild ? alwaysFirstChild.getBBox().height : 0);

      for (let child of this.draggableChildren()) {
        if (child !== skippedChild) {
          child.position(0, y, { parentRelative: true });
        }
        y += child.getBBox().height;
      }
    },

    /**
     * Returns the index at which the currently dragged child should end up if we dropped it now.
     */
    draggedChildNewIndex: function (draggedChild) {
      const draggableChildren = this.draggableChildren();
      let bottomOfTestedChild = this.paddingTop();
      const alwaysFirstChild = this.getAlwaysFirstChild(this.model.getEmbeddedCells());

      for (let i = 0; i < draggableChildren.length; i++) {
        bottomOfTestedChild += draggableChildren[i].getBBox().height;
        const topOfDraggedChild = draggedChild.position({ parentRelative: true }).y;

        let heightDeltaWithNextChild = 0;
        if (i + 1 < draggableChildren.length) {
          // let's compute the height difference between the dragged child and the next tested child 
          // (which will replace the dragged child if the next iteration ends the loop)
          heightDeltaWithNextChild = draggableChildren[i + 1].getBBox().height - draggedChild.getBBox().height;
        }
        if (topOfDraggedChild < bottomOfTestedChild + heightDeltaWithNextChild) {
          // if there's a child always first, we need to count it in new index of dragged child
          return i + (alwaysFirstChild ? 1 : 0);
        }
      }
      return draggableChildren.length - 1;
    },

    /**
     * Enforces that if a child needs to stay at the end then it does stay at the end
     * (for example the "user-input" answer of a question box),
     * and optionnally changes a child's index (when called during drag-and-drop)
     */
    reorderChildren: function (moveAChild = null) {
      let element = this.model;
      let children = element.getEmbeddedCells();
      children.forEach(child => element.unembed(child));
      if (moveAChild) {
        // thanks https://stackoverflow.com/a/5306832
        children.splice(moveAChild.newIndex, 0, children.splice(moveAChild.currentIndex, 1)[0]);
      }

      let alwaysFirstChild = this.getAlwaysFirstChild(children);
      let alwaysLastChild = this.getAlwaysLastChild(children);

      if (alwaysFirstChild) {
        element.embed(alwaysFirstChild);
      }
      for (let child of children) {
        if (!this.isChildAlwaysLast(child) && !this.isChildAlwaysLast(child)) {
          element.embed(child);
        }
      }
      if (alwaysLastChild) {
        element.embed(alwaysLastChild);
      }
      this.afterReorderChildren();
    },

    getAlwaysFirstChild: function (children) {
      let alwaysFirstChild = null;
      for (let child of children) {
        if (this.isChildAlwaysFirst(child)) {
          alwaysFirstChild = child;
        }
      }
      return alwaysFirstChild;
    },

    getAlwaysLastChild: function (children) {
      let alwaysLastChild = null;
      for (let child of children) {
        if (this.isChildAlwaysLast(child)) {
          alwaysLastChild = child;
        }
      }
      return alwaysLastChild;
    },

    /**
     * Entry point to the drad-and-drop mechanism.
     * Called when a child element is moved.
     * A new index is computed for the dragged child from its y position,
     * and if the new index is different from the current index of the dragged child,
     * then the children are reordered and repositionned
     * (with the exception that the currently dragged child isn't repositionned, obviously)
     */
    handleChildDragged: function (draggedChildView) {
      const draggedChild = draggedChildView.model;
      const embeddedCells = this.model.getEmbeddedCells();
      const currentIndex = embeddedCells.indexOf(draggedChild);
      const newIndex = this.draggedChildNewIndex(draggedChild);
      if (newIndex !== currentIndex) {
        this.reorderChildren({ currentIndex, newIndex });
        this.repositionOtherChildren(draggedChild);
      }
    },

  });
}
